"use client";

import { Plus } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheRuchniiStory() {
  const banner = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headline = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const proseReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const ticketReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const paragraphs = [{
    label: "\n",
    isFirst: true,
    body: "Ruchnii Studios began in a small room filled with extension cords, two high powered gaming PCs and the electricity blowing out from the overload. We didn't start with massive budgets or Hollywood connections; we started with a simple belief that it should be easy to make animations that uplift children. I spent years studying the frame-by-frame precision of classic animation, learning that every pixel, every movement, and every second of light matters. We built our foundation on the idea that technology should serve the story, not the other way around."
  }, {
    label: "§ II. THE VISION",
    isFirst: false,
    body: "I directed my first short film in a cramped studio, learning the hard way that passion without the right tools is just a dream. My mentors taught me that patience is the most important asset in animation. I ruined countless renders and scrapped dozens of storyboards before I understood the rhythm of a scene. That early work still sits in my digital archives—a reminder of the technical hurdles we had to overcome. Every project that leaves Ruchnii Studios today is measured against that same standard of excellence."
  }, {
    label: "“Blue is not decoration. It is a beacon to where the magic happens.”",
    isFirst: false,
    body: "People ask about our signature blue. They expect a story about gender, electricity, or artistic flare. The truth is more functional. Blue is the color of the 'Creative Spark'—the moment an idea becomes a reality. It is the color of a sunlit, cloudless sky in a rendered world, and the alert that tells you your render is complete. I didn't pick it to be flashy. I picked it to be a beacon. When you see blue on our platform, it means: this is where the magic happens."
  }, {
    label: "§ IV. NO FLUFF",
    isFirst: false,
    body: "Many platforms sell you a lifestyle. A beach house. A celebrity endorsement. A buzzword. We sell you a pipeline. It has a version history. It has a render queue. It has a clear path from concept to final export. Our assets are sourced from high-fidelity models we can verify. Our rendering engines are tested to performance counts we can prove. If a tool fails, we tell you why and we fix it. There is no fluff in our studio. There is only the software, the hardware, and the vision."
  }, {
    label: "§ V. THE PIPELINE",
    isFirst: false,
    body: "Years of building animation tools teach you that the romantic parts of the job are the smallest parts. Most of the day is debugging. Most of the day is tweaking one frame of one sequence for the tenth time because the lighting wasn't quite right. Most of the day is deleting work that didn't meet the bar. I don't say this to sound noble. I say it because I want you to know what you are investing in. You are paying for the iterations we discarded. You are paying for the perfection of the final frame."
  }, {
    label: "§ VI. OUR PROMISE",
    isFirst: false,
    body: "If you use Ruchnii Studios, here is what I owe you: a platform that performs as promised, a workflow you can rely on, and a team that will support your creative journey for years to come. That is the entire contract. No hidden agendas. No corporate jargon. Just the tools, the pipeline, and the promise that your vision is our priority."
  }, {
    label: "§ VII. SIGNED",
    isFirst: false,
    body: "I sign this the way I sign every project: with a commitment to quality, in the same place, every time: R.S. If you are ever in the virtual space, reach out. I will illumine for you the path of how a film is built. I won't give you a sales pitch. I will give you a seat at the table and the tools to start your own story."
  }];
  const specTicket = [{
    label: "FOUNDER",
    value: "R. RUCHNII"
  }, {
    label: "EXPERIENCE",
    value: "12 YEARS"
  }, {
    label: "SPECIALTY",
    value: "ANIMATION"
  }];
  const closingTicket = [{
    label: "WORDS",
    value: "1,920"
  }, {
    label: "READ TIME",
    value: "8 MIN"
  }, {
    label: "UPDATED",
    value: "2025.05"
  }];
  return <section data-section-id="1827" id="the-ruchnii-story" className="bg-background text-foreground py-28 md:py-44 relative overflow-hidden">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-8">
          <div className="lg:col-span-4">
            <div ref={headline.ref} className={`lg:sticky lg:top-24 relative transition-all duration-700 ease-out ${headline.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div aria-hidden="true" className="absolute -bottom-6 left-0 font-serif font-bold pointer-events-none select-none leading-none" style={{
              fontSize: "clamp(12rem, 22vw, 22rem)",
              color: "var(--foreground)",
              opacity: 0.08,
              letterSpacing: "-0.02em"
            }}>
                01
              </div>

              <h1 className="relative font-default font-bold text-foreground" style={{
              fontFamily: "var(--font-serif)",
              fontSize: "clamp(3rem, 7vw, 6rem)",
              lineHeight: 0.88,
              letterSpacing: "-0.01em"
            }}>
                <span className="block">WE BUILD</span>
                <span className="block">DREAMS.</span>
                <span className="relative inline-block"><span aria-hidden="true" className="absolute left-0 right-0 -bottom-2 h-3" data-ast-id="f:/src/pages/About/sections/TheRuchniiStory.tsx|sig:itlsvc" data-ast-start="7770" data-ast-end="7996" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, rgb(251, 189, 35) 0px, rgb(251, 189, 35) 10px, rgb(3, 7, 17) 10px, rgb(3, 7, 17) 20px)'
                }}></span></span>
              </h1>

              
            </div>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            <div ref={proseReveal.ref} className="flex flex-col">
              {paragraphs.map((p, i) => <div data-index={i} key={p.label} className={`transition-all duration-700 ease-out ${proseReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: proseReveal.isVisible ? `${i * 60}ms` : "0ms"
            }}>
                  <div className="flex items-center gap-3 pt-8 pb-6">
                    
                    <Separator className="flex-1 bg-border" />
                  </div>

                  {p.isFirst ? <p className="font-default text-foreground/85" style={{
                fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
                lineHeight: 1.62
              }}>
                      <span className="float-left mr-3 mt-2 font-bold text-primary" style={{
                  fontFamily: "var(--font-serif)",
                  fontSize: "clamp(4.5rem, 8vw, 6.5rem)",
                  lineHeight: 0.8
                }}>
                        {p.body.charAt(0)}
                      </span>
                      {p.body.slice(1)}
                    </p> : <p className="font-default text-foreground/85" style={{
                fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
                lineHeight: 1.62
              }}>
                      {p.body}
                    </p>}

                  {i === 1 && <figure className="relative mt-10">
                      <div className="relative" style={{
                  aspectRatio: "3 / 2"
                }}>
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/animation_pipeline_render.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/animation_pipeline_render.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/animation_pipeline_render.png-wvc-sizes" alt="Animation render process and digital storyboard" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <div className="absolute top-3 left-3 flex items-center gap-2 bg-background/85 px-2 py-1 border border-border">
                          <Plus className="size-3 text-primary" strokeWidth={1.5} />
                          <span className="font-mono text-[10px] uppercase text-foreground" style={{
                      letterSpacing: "0.12em"
                    }}>
                            STUDIO-RENDER-02
                          </span>
                        </div>
                      </div>
                      <figcaption className="mt-3 font-mono text-xs uppercase text-muted-foreground" style={{
                  letterSpacing: "0.06em"
                }}>
                        STUDIO-RENDER-02 .... Early storyboard, project RS-018, rev 02
                      </figcaption>
                    </figure>}

                  {i === 2 && <blockquote className="mt-10 pl-6 text-foreground" style={{
                borderLeft: "4px solid var(--primary)",
                fontFamily: "var(--font-serif)",
                fontSize: "clamp(1.75rem, 2.6vw, 2.5rem)",
                lineHeight: 1.0,
                fontWeight: 600,
                color: "var(--primary)",
                fontStyle: "italic"
              }}>
                      &ldquo;Blue is not decoration. It is a beacon to where the magic happens.&rdquo;
                    </blockquote>}

                  {i === 4 && <figure className="relative mt-10">
                      <div className="relative" style={{
                  aspectRatio: "3 / 2"
                }}>
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/character_concept_sketch.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/character_concept_sketch.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700347/img/character_concept_sketch.png-wvc-sizes" alt="Hand-drawn digital character design on tablet" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <div className="absolute top-3 left-3 flex items-center gap-2 bg-background/85 px-2 py-1 border border-border">
                          <Plus className="size-3 text-primary" strokeWidth={1.5} />
                          <span className="font-mono text-[10px] uppercase text-foreground" style={{
                      letterSpacing: "0.12em"
                    }}>
                            STUDIO-SKETCH-03
                          </span>
                        </div>
                      </div>
                      <figcaption className="mt-3 font-mono text-xs uppercase text-muted-foreground" style={{
                  letterSpacing: "0.06em"
                }}>
                        STUDIO-SKETCH-03 .... Character concept, model RS-018, rev 02
                      </figcaption>
                    </figure>}
                </div>)}
            </div>
          </div>
        </div>

        <div ref={ticketReveal.ref} className={`mt-24 md:mt-32 relative border border-border bg-card p-6 transition-all duration-700 ease-out ${ticketReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="absolute -top-2 left-4 px-2 bg-background">
            <span className="font-mono text-[10px] uppercase text-muted-foreground" style={{
            letterSpacing: "0.18em"
          }}>
              STUDIO TICKET
            </span>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-10">
            {closingTicket.map((row, i) => <li data-index={i} key={row.label} className="flex items-baseline gap-2 font-mono text-xs uppercase" style={{
            letterSpacing: "0.06em"
          }}>
                <span className="text-muted-foreground shrink-0">
                  {row.label}
                </span>
                <span className="flex-1 border-b border-dotted border-border translate-y-[-3px]" aria-hidden="true" />
                <span className="text-foreground shrink-0">{row.value}</span>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}